import React, { useEffect, useState, useCallback } from 'react';
import api, { formatRupiah } from '../../lib/api';
import { toast } from 'sonner';
import { Bank } from '@phosphor-icons/react';
import { useAuth } from '../../context/AuthContext';

const BANKS = ['BCA', 'Mandiri', 'BNI', 'BRI', 'CIMB Niaga', 'Permata', 'OCBC NISP', 'Danamon', 'BSI', 'Jago'];

export default function DashWithdrawals() {
  const { user, checkAuth } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ amount: 10000, bank_name: 'BCA', account_number: '', account_holder: '' });
  const [submitting, setSubmitting] = useState(false);
  const [balance, setBalance] = useState(0);

  const loadAll = useCallback(async () => {
    try {
      const [w, s] = await Promise.all([
        api.get('/withdrawals'),
        api.get('/dashboard/stats'),
      ]);
      setRows(w.data);
      setBalance(s.data.balance || 0);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.amount < 10000) { toast.error('Min Rp 10.000'); return; }
    if (form.amount > balance) { toast.error('Saldo tidak cukup'); return; }
    if (!form.account_number || !form.account_holder) { toast.error('Isi data bank'); return; }
    setSubmitting(true);
    try {
      await api.post('/withdrawals', form);
      toast.success('Pengajuan penarikan dikirim');
      setForm({ ...form, amount: 10000, account_number: '', account_holder: '' });
      await loadAll();
      await checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal');
    } finally { setSubmitting(false); }
  };

  const badge = (s) => {
    const map = { pending: 'bg-accent', approved: 'bg-secondary', rejected: 'bg-destructive text-white' };
    return <span className={`inline-block border-2 border-foreground px-2 py-0.5 text-xs font-bold uppercase ${map[s] || 'bg-muted'}`}>{s}</span>;
  };

  return (
    <div className="space-y-6 pt-2 md:pt-0" data-testid="dash-withdrawals">

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 border-2 border-foreground bg-white shadow-brutal-md p-5 md:p-6">
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold block mb-2">Jumlah (Rp)</label>
              <input type="number" min="10000" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseInt(e.target.value || '0') })}
                className="w-full border-2 border-foreground bg-white px-4 py-3 shadow-brutal-sm focus:outline-none focus:border-primary"
                required data-testid="wd-amount-input" />
              <p className="text-xs text-muted-foreground mt-1">Saldo tersedia: {formatRupiah(balance)}</p>
              <p className="text-xs text-muted-foreground mt-1 font-bold text-destructive">
                Tarif penarikan: 0,8% (Biaya: {formatRupiah(Math.round(form.amount * 0.008))} — Anda menerima: {formatRupiah(form.amount - Math.round(form.amount * 0.008))})
              </p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold block mb-2">Bank</label>
              <select value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                className="w-full border-2 border-foreground bg-white px-4 py-3 shadow-brutal-sm focus:outline-none focus:border-primary"
                data-testid="wd-bank-select">
                {BANKS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold block mb-2">Nomor Rekening</label>
              <input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                className="w-full border-2 border-foreground bg-white px-4 py-3 shadow-brutal-sm focus:outline-none focus:border-primary"
                required data-testid="wd-account-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold block mb-2">Nama Pemilik</label>
              <input value={form.account_holder} onChange={(e) => setForm({ ...form, account_holder: e.target.value })}
                className="w-full border-2 border-foreground bg-white px-4 py-3 shadow-brutal-sm focus:outline-none focus:border-primary"
                required data-testid="wd-holder-input" />
            </div>
            <button type="submit" disabled={submitting}
              className="inline-flex items-center gap-2 border-2 border-foreground bg-primary text-white px-5 py-3 font-bold uppercase tracking-wide shadow-brutal hover:shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] transition-all disabled:opacity-60"
              data-testid="wd-submit-btn">
              <Bank size={18} weight="bold" /> {submitting ? 'Memproses...' : 'Ajukan Penarikan'}
            </button>
          </form>
        </div>

        <aside className="border-2 border-foreground bg-primary text-white shadow-brutal-md p-5" data-testid="balance-card">
          <p className="text-xs uppercase tracking-widest font-bold opacity-90">Saldo</p>
          <p className="font-display text-3xl font-bold mt-2 leading-none">{formatRupiah(balance)}</p>
          <p className="text-xs mt-3 opacity-80">Min. Penarikan Rp 10.000</p>
        </aside>
      </div>

      <div className="border-2 border-foreground bg-white shadow-brutal-md overflow-hidden">
        <div className="p-5 border-b-2 border-foreground">
          <h2 className="font-display text-xl font-bold">Riwayat Penarikan</h2>
        </div>
        {loading ? <div className="p-6">Memuat...</div> : rows.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground">Belum ada pengajuan.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-background">
                <tr>
                  <th className="text-left p-3 font-bold uppercase text-xs">Jumlah Kotor</th>
                  <th className="text-left p-3 font-bold uppercase text-xs">Biaya (0.8%)</th>
                  <th className="text-left p-3 font-bold uppercase text-xs">Jumlah Bersih</th>
                  <th className="text-left p-3 font-bold uppercase text-xs">Bank</th>
                  <th className="text-left p-3 font-bold uppercase text-xs">Rekening</th>
                  <th className="text-left p-3 font-bold uppercase text-xs">Status</th>
                  <th className="text-left p-3 font-bold uppercase text-xs">Waktu</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((w) => {
                  const fee = w.fee !== undefined ? w.fee : Math.round(w.amount * 0.008);
                  const net = w.net_amount !== undefined ? w.net_amount : (w.amount - fee);
                  return (
                    <tr key={w.withdrawal_id} className="border-t-2 border-foreground" data-testid={`wd-row-${w.withdrawal_id}`}>
                      <td className="p-3 font-bold">{formatRupiah(w.amount)}</td>
                      <td className="p-3 text-muted-foreground">{formatRupiah(fee)}</td>
                      <td className="p-3 font-bold text-secondary">{formatRupiah(net)}</td>
                      <td className="p-3">{w.bank_name}</td>
                      <td className="p-3 font-mono text-xs">{w.account_number} <span className="text-muted-foreground">({w.account_holder})</span></td>
                      <td className="p-3">{badge(w.status)}</td>
                      <td className="p-3 text-xs text-muted-foreground">{new Date(w.created_at).toLocaleString('id-ID')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
